import type { Rect, Viewport } from "./tutorial-geometry";

/**
 * Looks up one anchor's element and returns its current rect, or `null` if
 * the element isn't mounted. Real (non-test) implementations query the DOM
 * by the anchor's `data-cb-tour` value; tests inject a fake `measure` that
 * returns canned rects instead, since jsdom has no real layout engine and
 * always reports zero-size rects regardless of actual CSS.
 */
export type Measure = (anchor: string) => Rect | null;

/**
 * Whether a resolved rect is actually spotlight-able. Two ways a "found"
 * rect can still be useless:
 *  - Zero (or negative) size — a `display:none` element, or a
 *    `visibility:hidden` ancestor whose subtree has collapsed to 0×0. Using
 *    it as-is would clip a hole at the origin, which reads as "the tour is
 *    pointing at nothing in the top-left corner."
 *  - Fully outside the viewport — e.g. scrolled away inside `<main>`'s own
 *    scroll container (see Layout.tsx), which has nothing to do with
 *    `window` and so nothing to do with a plain `rect.top`/`rect.left`
 *    check against zero alone.
 */
export function isUsableRect(rect: Rect, viewport: Viewport): boolean {
  if (rect.width <= 0 || rect.height <= 0) return false;
  if (rect.right <= 0 || rect.bottom <= 0) return false;
  if (rect.left >= viewport.width || rect.top >= viewport.height) return false;
  return true;
}

/**
 * Resolve a step's ordered anchor list (most-specific first) to the first
 * usable rect, falling through unusable candidates instead of stopping at
 * the first one found. Returns `null` when nothing in the list resolves —
 * the caller renders that step centered, with no spotlight, rather than
 * clipping a hole at the origin.
 */
export function resolveAnchor(
  anchors: readonly string[],
  measure: Measure,
  viewport: Viewport,
): Rect | null {
  for (const anchor of anchors) {
    const rect = measure(anchor);
    if (rect && isUsableRect(rect, viewport)) return rect;
  }
  return null;
}
